/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */
import { transformFirstArgument } from './transformFirstArgument';
import type { Selector, AnySelector, ComposedSelector, SelectorsMap, ComposedSelectorsMap } from './types';

/**
 * Abbreviations used in generic types:
 *   AppState - application state
 *   SliceState - slice state
 *   P - slice selector props
 *   S - any state
 *   R - any selector result
 *   M - map of selectors
 */

/**
 * @private
 * Create new selector that use the app slice selector in compose with slice state value selector
 * Checks all arguments abd throw errors if any of them has wrong type
 *
 * @param {SelectorSimple<S, R>} sliceSelector - selector for the slice state from the app state
 * @param {Selector<S, R, P?>} selector - local slice selector
 * @param {string} errorTitle - title for the thrown error
 * @param {string=} errorKey - optional name of local selector
 * @return {ComposedSelector<S, R, P>} new selector, that select get as argument the app state
 */
const bindSelectors = <AppState, SliceState, R, P extends {}>(
    sliceSelector: Selector<AppState, SliceState>,
    localSelector: AnySelector<SliceState, R, P>,
    errorTitle: string,
    errorKey?: string,
): ComposedSelector<AppState, R, P> => {
    if (typeof localSelector !== 'function') {
        throw new Error(
            `[${errorTitle}] wrong type (${typeof localSelector}) of selector${errorKey ? ` ${errorKey}` : ``}`,
        );
    }
    if (typeof sliceSelector !== 'function') {
        throw new Error(`[${errorTitle}] wrong type (${typeof sliceSelector}) of sliceSelector`);
    }

    return transformFirstArgument(localSelector as any, sliceSelector) as any;
};

/**
 * Create new selector that use the app slice selector in compose with slice state value selector
 *
 * @param {SelectorSimple<S, R>} sliceSelector - selector for the slice state from the app state
 * @param {Selector<S, R, P?>} selector - local slice selector
 * @return {ComposedSelector<S, R, P>} new selector, that select get as argument the app state
 */
export const composeSelector = <AppState, SliceState, R, P extends {}>(
    sliceSelector: Selector<AppState, SliceState>,
    selector: AnySelector<SliceState, R, P>,
): ComposedSelector<AppState, R, P> => bindSelectors(sliceSelector, selector, 'composeSelector');

/**
 * Bind all selectors in map object to basic slice state selector
 *
 * @param {SelectorSimple<AppState>} sliceSelector - selector for the slice state from the app state
 * @param {ComposedSelectorsMap<SliceState, M>} sliceSelectors - local slice selector
 * @return {ComposedSelectorsMap<AppState, M>} new selector, that select get as argument the app state
 */
export const composeSelectors = <S, M extends SelectorsMap>(
    sliceSelector: Selector<S>,
    sliceSelectors: M,
): ComposedSelectorsMap<S, M> => {
    if (typeof sliceSelector !== 'function') {
        throw new Error(`[composeSelectors] wrong type (${typeof sliceSelector}) of sliceSelector`);
    }

    return Object.keys(sliceSelectors).reduce(
        (acc, key: keyof M) => {
            acc[key] = bindSelectors(sliceSelector, sliceSelectors[key], 'composeSelectors', key as string) as any;

            return acc;
        },
        {} as ComposedSelectorsMap<S, M>,
    );
};
